import _ from 'lodash'
import { useParams } from 'react-router-dom'

import { LoadingContainer, Json, MiniTable } from 'peach/components'

import { Page } from 'core/components'
import { R } from 'core/helpers'
import { isNotFoundError } from 'core/helpers/error'
import {
  useGetLoanPeriods,
  useLoanExpectedPaymentsGet,
  useObligationsGetAll,
  useGetStatements,
} from 'core/types'

import DeferScheduleModal from '../ExpectedPayments/DeferScheduleModal'

const getToday = () => {
  const today = new Date()
  const dd = String(today.getDate()).padStart(2, '0')
  const mm = String(today.getMonth() + 1).padStart(2, '0') //January is 0!
  const yyyy = today.getFullYear()

  return `${yyyy}-${mm}-${dd}`
}

const today = getToday()

const LoanTimeline = () => {
  const { personId, loanId } = useParams<{
    personId?: string
    loanId?: string
  }>()

  const {
    data: periods,
    isLoading: periodsLoading,
    error: periodsError,
  } = useGetLoanPeriods({ personId, loanId })

  const {
    data: expectedPayments,
    isLoading: expPaymentsLoading,
    error: expPaymentsError,
  } = useLoanExpectedPaymentsGet({ personId, loanId })

  const {
    data: obligationsResp,
    isLoading: obligationsLoading,
    error: obligationsError,
  } = useObligationsGetAll({ personId, loanId })

  const {
    data: statements,
    isLoading: statementsLoading,
    error: statementsError,
  } = useGetStatements({
    personId,
    loanId,
    options: { throwOnError: R.isNot(isNotFoundError) },
  })

  const { schedule } = expectedPayments ?? {}

  const { obligations } = obligationsResp ?? {}

  const expPaymentsByPeriodId = _.groupBy(schedule, 'periodId')
  const obligationsByPeriodId = _.groupBy(obligations, 'periodId')
  const statementsByPeriodId = _.groupBy(statements, 'periodId')

  const results = _.map(periods, (period) => {
    const { id, startDate, endDate, dueDate, statementDate } = period
    const expPaymenents = expPaymentsByPeriodId[id]
    const statements = statementsByPeriodId[id]

    const obligations = obligationsByPeriodId[id]

    const obligation = _.first(obligations)

    const { isOpen, isFulfilled, isOverdue, obligationAmount } =
      obligation || {}

    const sequence =
      today > endDate ? 'past' : today >= startDate ? 'current' : 'future'

    const paymentStatus =
      dueDate < today
        ? isFulfilled
          ? 'paid'
          : 'overdue'
        : endDate < today
        ? isFulfilled
          ? 'fulfilled'
          : 'due'
        : startDate < today
        ? 'open'
        : 'future'

    return {
      id,
      today,
      startDate,
      endDate,
      dueDate,
      sequence,
      paymentStatus,
      obligationAmount,
      isOpen,
      isFulfilled,
      isOverdue,
      statementDate,
      obligations,
      obligationsCount: _.size(obligations),
      expPaymenentsCount: _.size(expPaymenents),
      statementsCount: _.size(statements),
    }
  })

  const rows = _.map(results, (result) => {
    const {
      id,
      startDate,
      endDate,
      dueDate,
      sequence,
      paymentStatus,
      obligationAmount,
    } = result
    return (
      <MiniTable.Row key={id}>
        {startDate}
        {endDate}
        {dueDate}
        {sequence}
        {`${paymentStatus} ${obligationAmount ?? ''}`}
      </MiniTable.Row>
    )
  })

  const isLoading =
    periodsLoading ||
    expPaymentsLoading ||
    obligationsLoading ||
    statementsLoading
  const error =
    periodsError ?? expPaymentsError ?? obligationsError ?? statementsError

  return (
    <LoadingContainer error={error} isLoading={isLoading}>
      <DeferScheduleModal />
      <Page variant='full'>
        <MiniTable
          columns={['Start', 'End', 'Due', 'Sequence', 'Payment Status']}
        >
          {rows}
        </MiniTable>
        <Json.Table json={results} title='Timeline' />
      </Page>
    </LoadingContainer>
  )
}

export default LoanTimeline
